<script setup>
import { defineProps, computed } from 'vue';
import Message from './Message.vue';
import { MESSAGE_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import MessageGroup from '../../components/widgets/conversation/MessageGroup.vue';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  isAPhoneChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
});

const allMessages = computed(() => {
  const messages = useCamelCase(props.messages, { deep: true });
  return messages.map(message => ({
    ...message,
    isAutomated:
      message.contentAttributes?.automationRuleId && !message.senderType,
  }));
});

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed' || current.private) return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  // Find in-reply-to message in the messages prop
  const replyMessage = props.messages?.find(
    message => message.id === inReplyToMessageId
  );

  return replyMessage ? useCamelCase(replyMessage) : null;
};

const groups = computed(() => {
  const message_groups = [];
  let lastMessageId = null;
  let group = [];

  allMessages.value.forEach(message => {
    const shouldGroup = message.private || message.isAutomated;

    if (shouldGroup) {
      if (!lastMessageId) {
        lastMessageId = message.id;
      }
      group.push(message);
    } else {
      if (group.length > 0) {
        message_groups.push(group);
      }
      message_groups.push([message]);
      group = [];
      lastMessageId = null;
    }
  });

  if (group.length > 0) {
    message_groups.push(group);
  }

  return message_groups;
});

const lastMessage = computed(() => {
  const messages = allMessages.value.filter(message => {
    return (
      (message.messageType === MESSAGE_TYPES.INCOMING ||
        message.messageType === MESSAGE_TYPES.OUTGOING) &&
      !message.isAutomated
    );
  });

  return messages[messages.length - 1];
});
</script>

<template>
  <ul class="px-4 bg-n-background">
    <slot name="beforeAll" />
    <template v-for="(group, index) in groups" :key="index">
      <MessageGroup
        :group="group"
        :last-message="lastMessage"
        :always-visible="isAPhoneChannel"
      >
        <template v-for="(message, msgIndex) in group" :key="message.id">
          <slot
            v-if="firstUnreadId && message.id === firstUnreadId"
            name="unreadBadge"
          />
          <Message
            v-bind="message"
            :is-email-inbox="isAnEmailChannel"
            :is-first-message="index === 0 && msgIndex === 0"
            :in-reply-to="getInReplyToMessage(message)"
            :group-with-next="shouldGroupWithNext(index, messages)"
            :inbox-supports-reply-to="inboxSupportsReplyTo"
            :current-user-id="currentUserId"
            data-clarity-mask="True"
          />
        </template>
      </MessageGroup>
    </template>
    <slot name="after" />
  </ul>
</template>
